<script setup lang="ts">
// Looping exploded-view animation of a paper framing build, used inside the framing dialog's
// "Mount / Side Depth" step. Both variants share the same layers (frame, glass, artwork, boards);
// only the spacer that slides in between glass and artwork differs: a mount (card border) or
// side depth (a raised lip built into the frame). Pure CSS, no state.
defineProps<{
  kind: 'mount' | 'sidedepth';
}>();
</script>

<template>
  <div class="fx-wrap" aria-hidden="true">
    <div class="fx">
      <div class="fx__scene">
        <div class="fx__stage">

          <!-- Backboard -->
          <div class="fx__block fx__backboard">
            <div class="fx__face fx__top" style="background:#D9C9A8"></div>
            <div class="fx__face fx__front" style="height:8px; background:#B7A67E"></div>
          </div>

          <!-- Mounting board -->
          <div class="fx__block fx__mounting-board">
            <div class="fx__face fx__top" style="background:#EFEAE1"></div>
            <div class="fx__face fx__front" style="height:10px; background:#CFC7B5"></div>
          </div>

          <!-- Artwork -->
          <div class="fx__block fx__artwork">
            <div class="fx__face fx__top" style="background:#F7F1E2">
              <svg viewBox="0 0 280 180" style="position:absolute; inset:0; width:100%; height:100%">
                <circle cx="195" cy="55" r="13" fill="none" stroke="#6B5B47" stroke-width="1.4" opacity="0.5"/>
                <path d="M50,138 L110,75 L150,110 L185,68 L232,138 Z" fill="none" stroke="#6B5B47" stroke-width="1.4" stroke-linejoin="round" opacity="0.55"/>
                <line x1="40" y1="138" x2="242" y2="138" stroke="#6B5B47" stroke-width="1.2" opacity="0.4"/>
              </svg>
            </div>
            <div class="fx__face fx__front" style="height:12px; background:#DACDAE"></div>
          </div>

          <!-- Mount: outer border only, hollow centre, slides in -->
          <div v-if="kind === 'mount'" class="fx__block fx__spacer">
            <div class="fx__face fx__top" style="inset:0; background:transparent; border:26px solid #D9CBA8; box-sizing:border-box; box-shadow:none"></div>
            <div class="fx__face fx__front" style="height:12px; background:#C9B98F"></div>
          </div>

          <!-- Side depth: four edge strips forming a continuous raised lip around the perimeter -->
          <div v-else class="fx__block fx__spacer">
            <div class="fx__pad" style="left:0; top:0; width:280px; height:12px">
              <div class="fx__face fx__top" style="background:#B08A63"></div>
              <div class="fx__face fx__front" style="height:38px; background:#96714A"></div>
            </div>
            <div class="fx__pad" style="left:0; top:168px; width:280px; height:12px">
              <div class="fx__face fx__top" style="background:#B08A63"></div>
              <div class="fx__face fx__front" style="height:38px; background:#96714A"></div>
            </div>
            <div class="fx__pad" style="left:0; top:12px; width:12px; height:156px">
              <div class="fx__face fx__top" style="background:#B08A63"></div>
              <div class="fx__face fx__front" style="height:38px; background:#96714A"></div>
            </div>
            <div class="fx__pad" style="left:268px; top:12px; width:12px; height:156px">
              <div class="fx__face fx__top" style="background:#B08A63"></div>
              <div class="fx__face fx__front" style="height:38px; background:#96714A"></div>
            </div>
          </div>

          <!-- Glass -->
          <div class="fx__block fx__glass">
            <div class="fx__face fx__top" style="background:#EAF1F6; opacity:.9"></div>
            <div class="fx__face fx__front" style="height:7px; background:#C9DCE6; opacity:.9"></div>
          </div>

          <!-- Frame -->
          <div class="fx__block fx__frame">
            <div class="fx__face fx__top" style="inset:0; background:transparent; border:34px solid #A47B54; box-sizing:border-box; box-shadow:none"></div>
            <div class="fx__face fx__front" style="height:20px; background:#8B6540"></div>
          </div>

        </div>
      </div>
    </div>

    <!-- Flat (non-3D) label list beside the scene. Faded in only while the exploded layers are
         sitting at their fully-separated plateau (58%-80% of the loop), then faded out again, so
         labels never overlap mid-motion. Each row's "top" is the layer's own computed on-screen
         position at that plateau (projected through the scene's rotation/perspective/scale), so a
         row lines up with its layer instead of being evenly spaced. -->
    <div class="fx__labels">
      <div class="fx__row" style="top:40px"><span class="fx__tick"></span><span class="fx__name">Frame</span></div>
      <div class="fx__row" style="top:65px"><span class="fx__tick"></span><span class="fx__name">Glass</span></div>
      <div class="fx__gap" style="top:88px">&uarr; air gap &darr;</div>
      <div class="fx__row fx__row--accent" style="top:110px">
        <span class="fx__tick"></span>
        <span class="fx__name">{{ kind === 'mount' ? 'Mount' : 'Side depth' }}</span>
      </div>
      <div class="fx__row" style="top:139px"><span class="fx__tick"></span><span class="fx__name">Artwork</span></div>
      <div class="fx__row" style="top:159px"><span class="fx__tick"></span><span class="fx__name">Mounting board</span></div>
      <div class="fx__row" style="top:172px"><span class="fx__tick"></span><span class="fx__name">Backboard</span></div>
    </div>
  </div>
</template>

<style scoped>
.fx-wrap {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

/* Fixed-size 3D scene (340 x 480) scaled down to sit beside the label list within a card; the
   wrapper crops to the area the exploded layers actually travel through. Height/offset give a
   margin above the frame's highest point and below the backboard's lowest point so neither clips. */
.fx {
  position: relative;
  width: 140px;
  flex: none;
  height: 220px;
  overflow: hidden;
}
.fx__scene {
  position: absolute;
  left: 50%;
  top: 0;
  width: 340px;
  height: 480px;
  perspective: 1500px;
  transform-origin: top center;
  transform: translate(-50%, 29px) scale(0.35);
}
.fx__stage {
  position: relative;
  width: 280px;
  height: 180px;
  margin: 200px auto 0;
  transform-style: preserve-3d;
  transform: rotateX(58deg) rotateZ(42deg);
}
.fx__block {
  position: absolute;
  left: 0;
  top: 0;
  width: 280px;
  height: 180px;
  transform-style: preserve-3d;
}
.fx__face { position: absolute; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15); }
.fx__top { inset: 0; border-radius: 4px; }
.fx__front { left: 0; top: 100%; width: 100%; transform-origin: top; }
.fx__pad { position: absolute; }
.fx__pad .fx__top { inset: 0; border-radius: 3px; }
.fx__pad .fx__front { left: 0; top: 100%; width: 100%; transform-origin: top; }

/* Frame, glass, boards all expand outward while the spacer slides in: adding a mount or side
   depth genuinely deepens the whole assembly. */
@keyframes fxFrame {
  0%, 8%   { transform: translateZ(40px); }
  38%      { transform: translateZ(220px); }
  58%, 80% { transform: translateZ(280px); }
  98%      { transform: translateZ(220px); }
  100%     { transform: translateZ(40px); }
}
@keyframes fxGlass {
  0%, 8%   { transform: translateZ(32px); }
  38%      { transform: translateZ(170px); }
  58%, 80% { transform: translateZ(210px); }
  98%      { transform: translateZ(170px); }
  100%     { transform: translateZ(32px); }
}
@keyframes fxArtwork {
  0%, 8%    { transform: translateZ(20px); }
  38%, 80%  { transform: translateZ(-30px); }
  98%, 100% { transform: translateZ(20px); }
}
@keyframes fxMountingBoard {
  0%, 8%   { transform: translateZ(8px); }
  38%      { transform: translateZ(-70px); }
  58%, 80% { transform: translateZ(-100px); }
  98%      { transform: translateZ(-70px); }
  100%     { transform: translateZ(8px); }
}
@keyframes fxBackboard {
  0%, 8%   { transform: translateZ(0px); }
  38%      { transform: translateZ(-110px); }
  58%, 80% { transform: translateZ(-150px); }
  98%      { transform: translateZ(-110px); }
  100%     { transform: translateZ(0px); }
}
@keyframes fxSpacer {
  0%, 38%   { transform: translateX(-260px) translateZ(70px); opacity: 0; }
  58%, 80%  { transform: translateX(0) translateZ(70px); opacity: 1; }
  98%, 100% { transform: translateX(-260px) translateZ(70px); opacity: 0; }
}
@keyframes fxLabelsFade {
  0%, 56%   { opacity: 0; }
  62%, 76%  { opacity: 1; }
  80%, 100% { opacity: 0; }
}
.fx__frame          { animation: fxFrame 8s ease-in-out infinite; }
.fx__glass          { animation: fxGlass 8s ease-in-out infinite; }
.fx__spacer         { animation: fxSpacer 8s ease-in-out infinite; }
.fx__artwork        { animation: fxArtwork 8s ease-in-out infinite; }
.fx__mounting-board { animation: fxMountingBoard 8s ease-in-out infinite; }
.fx__backboard      { animation: fxBackboard 8s ease-in-out infinite; }

/* Reduced motion: hold the fully exploded pose (and the labels) instead of looping. */
@media (prefers-reduced-motion: reduce) {
  .fx__frame, .fx__glass, .fx__spacer, .fx__artwork, .fx__mounting-board, .fx__backboard,
  .fx__labels {
    animation-play-state: paused;
    animation-delay: -5s;
  }
}

/* Flat label list — intentionally not part of the rotated .fx__stage, so the text itself is
   never tilted. Sits beside the scene, same height as it, with each row absolutely positioned at
   its layer's own projected screen position (see the template comment) instead of evenly spaced. */
.fx__labels {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 220px;
  animation: fxLabelsFade 8s ease-in-out infinite;
}
.fx__row {
  position: absolute;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 6px;
}
.fx__tick { width: 10px; height: 1px; background: #9ca3af; flex: none; }
.fx__row--accent .fx__tick { background: #d97706; }
.fx__name { font-size: 11px; color: #374151; }
.fx__row--accent .fx__name { color: #b45309; font-weight: 600; }
.fx__gap {
  position: absolute;
  left: 16px;
  right: 0;
  transform: translateY(-50%);
  font-size: 9px;
  color: #d97706;
  font-style: italic;
}
</style>
